
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Shield } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { LocationState, MultiProductFormData, SharedFieldsFormData, ContactDetailsFormData, FormData } from "@/types/forms";
import { SubmissionService } from "@/services/submissionService";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/contexts/TenantContext";
import SharedFieldsForm from "@/components/quote-forms/SharedFieldsForm";
import StickyNavigation from "@/components/layout/StickyNavigation";
import PropertyAllRisksForm from "@/components/quote-forms/PropertyAllRisksForm";
import ThirdPartyLiabilityForm from "@/components/quote-forms/ThirdPartyLiabilityForm";
import WorkersCompensationForm from "@/components/quote-forms/WorkersCompensationForm";
import EmployeeMedicalForm from "@/components/quote-forms/EmployeeMedicalForm";
import CyberInsuranceForm from "@/components/quote-forms/CyberInsuranceForm";
import FleetInsuranceForm from "@/components/quote-forms/FleetInsuranceForm";
import ProfessionalIndemnityForm from "@/components/quote-forms/ProfessionalIndemnityForm";
import BusinessInterruptionForm from "@/components/quote-forms/BusinessInterruptionForm";
import ProductLiabilityForm from "@/components/quote-forms/ProductLiabilityForm";
import ContactDetailsForm from "@/components/quote-forms/ContactDetailsForm";
import TenantFooter from "@/components/layout/TenantFooter";

// Product slug mapping between decision tree and form components
const PRODUCT_SLUG_MAPPING = {
  // Decision tree slug -> Form component slug (these are the same as database slugs now)
  'employee-medical': 'employee-medical',
  'workmens-comp': 'workers-compensation',
  'public-liability': 'third-party-liability',
  'property-all-risks': 'property-all-risks',
  'cyber-protection': 'cyber-insurance',
  'fleet-insurance': 'fleet-insurance',
  'professional-indemnity': 'professional-indemnity',
  'business-interruption': 'business-interruption',
  'product-liability': 'product-liability'
};

// Reverse mapping for submission (form component slug -> database slug)
const FORM_TO_DB_SLUG_MAPPING = Object.fromEntries(
  Object.entries(PRODUCT_SLUG_MAPPING).map(([dbSlug, formSlug]) => [formSlug, dbSlug])
);

// Additional mapping for invalid/test slugs to correct database slugs
const INVALID_TO_DB_SLUG_MAPPING = {
  'employee-medical-insurance': 'employee-medical',
  'workers-compensation-insurance': 'workmens-comp',
  'third-party-liability-insurance': 'public-liability',
  'property-all-risks-insurance': 'property-all-risks',
  'cyber-insurance': 'cyber-protection',
  'fleet-insurance': 'fleet-insurance',
  'professional-indemnity-insurance': 'professional-indemnity',
  'business-interruption-insurance': 'business-interruption',
  'product-liability-insurance': 'product-liability'
};

// Function to normalize product slugs to database format
const normalizeProductSlug = (slug: string): string => {
  console.log(`🔧 Normalizing slug: "${slug}"`);

  // If it's already a valid database slug, return as-is
  if (Object.keys(PRODUCT_SLUG_MAPPING).includes(slug)) {
    console.log(`✅ Already valid DB slug: "${slug}"`);
    return slug;
  }

  // If it's an invalid slug, map it to the correct database slug
  if (INVALID_TO_DB_SLUG_MAPPING[slug]) {
    const mapped = INVALID_TO_DB_SLUG_MAPPING[slug];
    console.log(`🔄 Mapped invalid slug: "${slug}" → "${mapped}"`);
    return mapped;
  }

  // If it's a form component slug, map it to database slug
  if (FORM_TO_DB_SLUG_MAPPING[slug]) {
    const mapped = FORM_TO_DB_SLUG_MAPPING[slug];
    console.log(`🔄 Mapped form slug: "${slug}" → "${mapped}"`);
    return mapped;
  }

  // Return as-is if no mapping found (will likely cause an error, but that's expected)
  console.log(`⚠️ No mapping found for: "${slug}"`);
  return slug;
};

const productTitles = {
  "property-all-risks": "Property All Risks Insurance",
  "public-liability": "Third Party Liability Insurance",
  "workmens-comp": "Workers Compensation Insurance",
  "employee-medical": "Employee Medical Insurance",
  "cyber-protection": "Cyber Insurance",
  "fleet-insurance": "Fleet Insurance",
  "professional-indemnity": "Professional Indemnity Insurance",
  "business-interruption": "Business Interruption Insurance",
  "product-liability": "Product Liability Insurance"
};

const MultiProductQuote = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { tenant } = useTenant();

  // Tenant-aware prefix for navigation
  const pathMatch = location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = pathMatch ? `/site/${pathMatch[1]}` : '';
  const { selectedProducts: rawSelectedProducts = [] } = (location.state as LocationState) || {};

  // Normalize product slugs to ensure they match database format
  const selectedProducts = rawSelectedProducts.map(normalizeProductSlug);

  // Debug logging
  console.log('🔍 MultiProductQuote Debug v2.0 (FIXED MAPPINGS):', {
    rawSelectedProducts,
    normalizedSelectedProducts: selectedProducts,
    locationState: location.state,
    mappingVersion: 'v2.0-hyphen-slugs'
  });

  const [formData, setFormData] = useState<MultiProductFormData>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [showSharedFields, setShowSharedFields] = useState(true);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // If no products are selected, redirect to decision tree (tenant-aware)
  if (selectedProducts.length === 0) {
    navigate(`${subPrefix}/decision-tree`);
    return null;
  }

  const handleSharedFieldsSubmit = (data: SharedFieldsFormData) => {
    setFormData(prev => ({ ...prev, sharedFields: data }));
    setShowSharedFields(false);
  };

  const handleProductFormSubmit = (productType: string, data: FormData) => {
    setFormData(prev => ({ ...prev, [productType]: data }));

    // Automatically advance to next step after form submission
    if (currentStep < selectedProducts.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      // Last product, check if all forms are completed before moving to contact details
      const updatedFormData = { ...formData, [productType]: data };
      const allFormsCompleted = selectedProducts.every(productSlug => {
        const productData = updatedFormData[productSlug as keyof MultiProductFormData];
        return productData && Object.keys(productData).length > 0;
      });

      if (allFormsCompleted) {
        setShowContactDetails(true);
        window.scrollTo(0, 0);
      } else {
        toast({
          title: "Please complete all forms",
          description: "All product forms must be completed before proceeding to contact details.",
          variant: "destructive",
        });
      }
    }
  };



  const handleBack = () => {
    if (showContactDetails) {
      setShowContactDetails(false);
      window.scrollTo(0, 0);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    } else if (!showSharedFields) {
      setShowSharedFields(true);
      window.scrollTo(0, 0);
    }
  };

  const handleBackToConfirmation = () => {
    // Navigate back to confirmation page with state (tenant-aware)
    navigate(`${subPrefix}/confirmation`, {
      state: location.state
    });
  };

  const handleContactSubmit = async (contactData: ContactDetailsFormData) => {
    setIsSubmitting(true);

    try {
      // Validate that all product forms have been completed
      const missingProducts = selectedProducts.filter(productSlug => {
        const productData = formData[productSlug as keyof MultiProductFormData];
        return !productData || Object.keys(productData).length === 0;
      });

      if (missingProducts.length > 0) {
        toast({
          title: "Incomplete Forms",
          description: `Please complete the forms for: ${missingProducts.join(', ')}`,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Prepare products data for submission
      const productsData = selectedProducts.map(productSlug => ({
        slug: productSlug,
        formData: {
          ...formData.sharedFields,
          ...formData[productSlug as keyof MultiProductFormData]
        }
      }));

      // Debug logging
      console.log('🔍 Submission Debug:', {
        selectedProducts,
        productsData: productsData.map(p => ({ slug: p.slug, hasFormData: Object.keys(p.formData).length > 0 }))
      });

      // Use tenant if available; in local dev we may skip fetching tenant and fall back to default
      const tenantId = tenant?.id;
      console.log('🚀 Submitting multi-product quote:', { productsData, contactData, tenantId: tenantId || 'auto-detect' });

      await SubmissionService.createMultiProductSubmission(
        productsData,
        contactData,
        tenantId
      );

      toast({
        title: "Quote Submitted Successfully!",
        description: "We'll be in touch within 24 hours with your personalized quotes.",
      });

      navigate(`${subPrefix}/quote-submitted`, {
        state: {
          formData: { ...formData, contactDetails: contactData },
          submitted: true
        }
      });
    } catch (error) {
      console.error('Error submitting multi-product quote:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your quote. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProductForm = (productType: string) => {
    // Determine button text based on current step
    const isLastProduct = currentStep >= selectedProducts.length - 1;
    const buttonText = isLastProduct ? "Continue to Contact Details" : "Next Product";

    const commonProps = {
      onSubmit: (data: FormData) => handleProductFormSubmit(productType, data),
      initialData: formData[productType as keyof MultiProductFormData] || {},
      buttonText
    };

    switch (productType) {
      case "property-all-risks":
        return <PropertyAllRisksForm {...commonProps} />;
      case "public-liability":
        return <ThirdPartyLiabilityForm {...commonProps} />;
      case "workmens-comp":
        return <WorkersCompensationForm {...commonProps} />;
      case "employee-medical":
        return <EmployeeMedicalForm {...commonProps} />;
      case "cyber-protection":
        return <CyberInsuranceForm {...commonProps} />;
      case "fleet-insurance":
        return <FleetInsuranceForm {...commonProps} />;
      case "professional-indemnity":
        return <ProfessionalIndemnityForm {...commonProps} />;
      case "business-interruption":
        return <BusinessInterruptionForm {...commonProps} />;
      case "product-liability":
        return <ProductLiabilityForm {...commonProps} />;
      default:
        return <div>Form not available for this product type</div>;
    }
  };

  const currentProduct = showContactDetails ? null : (showSharedFields ? null : selectedProducts[currentStep]);
  const totalSteps = selectedProducts.length + 2; // +2 for shared fields and contact details
  const currentStepNumber = showSharedFields ? 1 : (showContactDetails ? totalSteps : currentStep + 2);
  const progress = (currentStepNumber / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <StickyNavigation />
      <div className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <Button
            variant="ghost"
            onClick={showSharedFields ? handleBackToConfirmation : handleBack}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {showSharedFields ? "Back to Assessment" : "Previous Step"}
          </Button>

          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete your Quote</h1>
            <p className="text-xl text-gray-600 mb-4">
              {showSharedFields ? "General Business Information" : 
               showContactDetails ? "Final Step: Your Contact Details" : 
               `${productTitles[currentProduct as keyof typeof productTitles]}`}
            </p>
            <div className="text-sm text-gray-500">
              Step {currentStepNumber} of {totalSteps}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Form Content */}
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>
                {showSharedFields ? "Business Information" : 
                 showContactDetails ? "Contact Details" : 
                 "Product Information"}
              </CardTitle>
              {!showSharedFields && !showContactDetails && (
                <div className="flex items-center mt-2">
                  <Shield className="h-4 w-4 text-blue-600 mr-2" />
                  <Badge variant="outline" className="font-normal">
                    {currentStep + 1} of {selectedProducts.length}
                  </Badge>
                </div>
              )}
            </div>
            {!showSharedFields && !showContactDetails && selectedProducts.length > 1 && (
              <Badge className={`px-3 py-1 ${currentStep < selectedProducts.length - 1 ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                {currentStep < selectedProducts.length - 1 ? 'More to come' : 'Last product'}
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            {showSharedFields ? (
              <SharedFieldsForm 
                onSubmit={handleSharedFieldsSubmit} 
                initialData={formData.sharedFields || {}}
              />
            ) : showContactDetails ? (
              <ContactDetailsForm
                onSubmit={handleContactSubmit}
                initialData={formData.contactDetails || {}}
                isSubmitting={isSubmitting}
              />
            ) : (
              renderProductForm(currentProduct)
            )}
          </CardContent>
        </Card>

        {/* Selected Products Summary */}
        {!showSharedFields && (
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Your Selected Products</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {selectedProducts.map((productType: string, index: number) => (
                <div 
                  key={productType}
                  className={`flex items-center p-3 rounded-lg border ${
                    index === currentStep && !showContactDetails 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-200'
                  }`}
                >
                  <div 
                    className={`w-6 h-6 rounded-full mr-3 flex items-center justify-center text-xs font-semibold ${
                      index === currentStep && !showContactDetails 
                        ? 'bg-blue-600 text-white' 
                        : index < currentStep 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <span className="text-sm">{productTitles[productType as keyof typeof productTitles]}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
      {/* Tenant-aware footer to match homepage */}
      <TenantFooter />
    </div>
  );
};

export default MultiProductQuote;
